import React, { useCallback, useEffect, useState } from "react";
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import FaceIcon from '@mui/icons-material/Face';
import { translations } from "../translations/common";
import {
  RootContainer,
} from "./styles/HomeStyles";
import { FilterDrawer } from "../components/common/FilterDrawer";
import ReminderTable from "../components/reminder/ReminderTable";
import ReminderFilterForm from "../components/reminder/ReminderFilterForm";
import { useLocation } from "react-router-dom";
import ReminderModel, { ReminderView, ReminderData, parseFilter } from "../core/api/reminder/ReminderModel";
import { useNotifications } from "../hooks/NotificationProvider";
import ReminderModalForm from "../components/reminder/ReminderModalForm";
import ClientStateModalForm from "../components/client/ClientStateModalForm";
import EntityId from "../core/api/EntityId";
import { Box,  } from "@mui/material";

import { PersistentSearchHeader } from "../components/PersistentSearchHeader";
import { parsePagination } from "core/PaginationUtils";

export const Home: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [modifyReminders, setModifyReminders] = useState(false);
  const [modifyClients, setModifyClients] = useState(false);
  const [reminders, setReminders] = useState<ReminderView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const notifications = useNotifications();

  const fetchReminders = useCallback(async () => {
    try {
      setIsLoading(true);
      const pagination = parsePagination(location.search);
      const filter = parseFilter(location.search);
      const response = await ReminderModel.listAll({ page: pagination.page, size: pagination.size }, filter);
      setReminders(response.content);
      setTotalCount(response.page.totalElements);
    } catch (error) {
      notifications.notify({
        message: translations.error.generic,
        level: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [location.search, notifications]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Create a custom save function that returns EntityId
  const handleSaveReminder = async (model: ReminderData): Promise<EntityId> => {
    const filter = parseFilter(location.search);
    await ReminderModel.updateBulk(model, filter);
    fetchReminders();
    return { id: 'bulk-update' };
  };

  // Create a custom save function for client state
  const handleSaveClientState = async (state: string): Promise<number> => {
    const filter = parseFilter(location.search);
    const count = await ReminderModel.updateClientStates(state, filter);
    fetchReminders();
    return count;
  };

  const filterContent = (
    <ReminderFilterForm/>
  );

  const tableActions = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <IconButton
        onClick={() => setModifyReminders(true)}
        size="small"
        aria-label={translations.home.editReminders}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        onClick={() => setModifyClients(true)}
        size="small"
        aria-label={translations.home.editClients}
      >
        <FaceIcon />
      </IconButton>
    </Box>
  );

  return (
    <RootContainer>
      <FilterDrawer
        open={isDrawerOpen}
        onToggle={toggleDrawer}
        filterContent={filterContent}
      >
              <ReminderTable
                customHeader={
                  <PersistentSearchHeader
                    title="Priminimai"
                    actions={tableActions}
                  />
                }
                isLoading={isLoading}
                reminders={reminders}
                totalCount={totalCount}
                refresh={fetchReminders}
              />
      </FilterDrawer>

      {modifyReminders && (
        <ReminderModalForm
          value={ReminderModel.defaultValue()}
          save={handleSaveReminder}
          close={() => {
            setModifyReminders(false);
          }}
        />
      )}

      {modifyClients && (
        <ClientStateModalForm
          save={handleSaveClientState}
          close={() => setModifyClients(false)}
        />
      )}
    </RootContainer>
  );
};
